import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useRecoilValue } from 'recoil';
import { authzContextAtom } from '../../../../authorization/authzState';
import { userInformationAtom } from '../../../../Services/_states';
import { ConstantActions } from '../../../../Services/_actions/index.js';
import { canCreateWorkspaceTable } from '../../utils/canCreateWorkspaceTable';
import {
  buildCreateFormPayload,
  buildUpdateFormPayload,
  hydrateLegacyFormResponse,
} from '../adapters';
import { FORM_STUDIO_COPY } from '../constants/formStudioConfig';
import formStudioService from '../services/formStudioService';
import {
  extractFormStudioApiError,
  isFormStudioApiSuccess,
} from '../utils/formStudioApiErrors';
import { extractCreatedFormId } from '../utils/formStudioCreateFlow';
import {
  createEmptyFormState,
  getNextFieldItemId,
  normalizeFormSnapshot,
} from '../utils/formStudioFormUtils';
import { validateFormStudioForm } from '../utils/formStudioValidation';
import { hydrateFormFieldsFromStatusApi } from '../adapters/statusFieldSyncAdapter';
import statusWorkflowService from '../services/statusWorkflowService';
import useStatusFieldSync from './useStatusFieldSync';

function snapshotsEqual(left, right) {
  return JSON.stringify(left) === JSON.stringify(right);
}

export default function useFormStudio({
  mode = 'create',
  workspaceId,
  accountId,
  formId,
  enabled = true,
} = {}) {
  const authz = useRecoilValue(authzContextAtom);
  const userInfo = useRecoilValue(userInformationAtom);
  const { userHasPermission } = ConstantActions();
  const [form, setForm] = useState(createEmptyFormState);
  const [baseline, setBaseline] = useState(createEmptyFormState);
  const [fieldErrors, setFieldErrors] = useState({});
  const [formErrors, setFormErrors] = useState({});
  const [validationSummary, setValidationSummary] = useState([]);
  const [isLoading, setIsLoading] = useState(mode === 'edit');
  const [loadError, setLoadError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const isMountedRef = useRef(true);
  const { syncFieldStatuses } = useStatusFieldSync();

  useEffect(() => () => {
    isMountedRef.current = false;
  }, []);

  const isDirty = useMemo(
    () => !snapshotsEqual(normalizeFormSnapshot(form), normalizeFormSnapshot(baseline)),
    [baseline, form]
  );

  const applyHydratedForm = useCallback((hydrated) => {
    const nextForm = {
      ...createEmptyFormState(),
      ...hydrated,
      leads_form_items: hydrated?.leads_form_items ?? [],
    };
    setForm(nextForm);
    setBaseline(normalizeFormSnapshot(nextForm));
    setFieldErrors({});
    setFormErrors({});
    setValidationSummary([]);
    setSubmitError('');
  }, []);

  const loadForm = useCallback(async () => {
    if (!enabled || mode !== 'edit' || !workspaceId || !formId) return;

    setIsLoading(true);
    setLoadError('');

    try {
      const response = await formStudioService.getForm(workspaceId, formId);
      let hydrated = hydrateLegacyFormResponse(response, { formId });
      if (!hydrated) {
        throw new Error('Form not found.');
      }

      try {
        const statusResponse = await statusWorkflowService.fetchStatuses({ leadFormId: formId });
        const apiStatuses = Array.isArray(statusResponse?.data) ? statusResponse.data : [];
        if (apiStatuses.length) {
          hydrated = {
            ...hydrated,
            leads_form_items: hydrateFormFieldsFromStatusApi(
              hydrated.leads_form_items ?? [],
              apiStatuses
            ),
          };
        }
      } catch {
        // Form still loads if status API fails; sync on save can recover.
      }

      if (isMountedRef.current) {
        applyHydratedForm(hydrated);
      }
    } catch (error) {
      if (isMountedRef.current) {
        setLoadError(error?.message || 'Failed to load form.');
      }
    } finally {
      if (isMountedRef.current) {
        setIsLoading(false);
      }
    }
  }, [applyHydratedForm, enabled, formId, mode, workspaceId]);

  useEffect(() => {
    if (mode === 'create') {
      const empty = createEmptyFormState();
      setForm(empty);
      setBaseline(normalizeFormSnapshot(empty));
      setLoadError('');
      setIsLoading(false);
      return;
    }
    loadForm();
  }, [loadForm, mode]);

  const updateFormMeta = useCallback((patch) => {
    setForm((prev) => ({ ...prev, ...patch }));
    setSubmitError('');
    if (patch.leads_form_name != null) {
      setFormErrors((prev) => ({ ...prev, leads_form_name: '' }));
    }
  }, []);

  const setFields = useCallback((nextFields) => {
    setForm((prev) => ({
      ...prev,
      leads_form_items: nextFields.map((field, index) => ({
        ...field,
        order_id: index + 1,
      })),
    }));
    setFieldErrors({});
    setSubmitError('');
  }, []);

  const updateField = useCallback((index, patch) => {
    setForm((prev) => {
      const fields = [...(prev.leads_form_items || [])];
      if (!fields[index]) return prev;
      fields[index] = { ...fields[index], ...patch };
      return { ...prev, leads_form_items: fields };
    });
    setFieldErrors((prev) => ({ ...prev, [index]: undefined }));
    setSubmitError('');
  }, []);

  const addField = useCallback((field = {}) => {
    setForm((prev) => {
      const fields = [...(prev.leads_form_items || [])];
      const itemId = getNextFieldItemId(fields);
      fields.push({
        key_name: '',
        alias_name: '',
        key_type: 'STRING',
        ui_key_type: 'STRING',
        statusOption: '',
        status_parent_key: '',
        is_encrypted: false,
        groupId: '',
        order_id: fields.length + 1,
        item_id: itemId,
        ...field,
      });
      return { ...prev, leads_form_items: fields };
    });
    setSubmitError('');
  }, []);

  const removeField = useCallback((index) => {
    setForm((prev) => ({
      ...prev,
      leads_form_items: (prev.leads_form_items || []).filter((_, itemIndex) => itemIndex !== index),
    }));
    setFieldErrors({});
    setSubmitError('');
  }, []);

  const duplicateField = useCallback((index) => {
    setForm((prev) => {
      const fields = [...(prev.leads_form_items || [])];
      const source = fields[index];
      if (!source) return prev;
      const itemId = getNextFieldItemId(fields);
      fields.push({
        ...source,
        item_id: itemId,
        order_id: fields.length + 1,
        key_name: source.key_name ? `${source.key_name}_copy` : '',
      });
      return { ...prev, leads_form_items: fields };
    });
    setSubmitError('');
  }, []);

  const reorderFields = useCallback((fromIndex, toIndex) => {
    if (fromIndex === toIndex) return;
    setForm((prev) => {
      const fields = [...(prev.leads_form_items || [])];
      if (
        fromIndex < 0
        || toIndex < 0
        || fromIndex >= fields.length
        || toIndex >= fields.length
      ) {
        return prev;
      }
      const [moved] = fields.splice(fromIndex, 1);
      if (!moved) return prev;
      fields.splice(toIndex, 0, moved);
      return {
        ...prev,
        leads_form_items: fields.map((field, index) => ({
          ...field,
          order_id: index + 1,
        })),
      };
    });
    setFieldErrors({});
    setSubmitError('');
  }, []);

  const updateGroups = useCallback((groupOptions) => {
    setForm((prev) => ({ ...prev, group_options: groupOptions }));
    setSubmitError('');
  }, []);

  const updateHotCriteria = useCallback((criteria) => {
    setForm((prev) => ({ ...prev, global_hot_lead_criteria: criteria }));
    setSubmitError('');
  }, []);

  const updateTemplates = useCallback((templates) => {
    setForm((prev) => ({ ...prev, templates }));
    setSubmitError('');
  }, []);

  const deletePersistedField = useCallback(async (index) => {
    const field = form.leads_form_items?.[index];
    if (!field || mode !== 'edit' || !formId) {
      removeField(index);
      return { success: true };
    }

    try {
      const response = await formStudioService.deleteField(formId, field.item_id);
      if (response?.status === 200) {
        removeField(index);
        setBaseline((prev) => ({
          ...prev,
          leads_form_items: (prev.leads_form_items || []).filter((_, itemIndex) => itemIndex !== index),
        }));
        return { success: true };
      }
      return { success: false, error: 'Form field was not deleted.' };
    } catch (error) {
      return { success: false, error: error?.message || 'Failed to delete form field.' };
    }
  }, [form.leads_form_items, formId, mode, removeField]);

  const validate = useCallback(() => {
    const result = validateFormStudioForm(form, { hasFormId: Boolean(formId) });
    setFormErrors(result.formErrors);
    setFieldErrors(result.fieldErrors);
    setValidationSummary(result.errorSummary);
    return result;
  }, [form]);

  const save = useCallback(async () => {
    const validation = validate();
    if (!validation.isValid) {
      return { success: false, validation };
    }

    if (!workspaceId) {
      const error = 'Workspace ID is required.';
      setSubmitError(error);
      return { success: false, error };
    }

    setIsSubmitting(true);
    setSubmitError('');

    if (mode === 'create') {
      const canCreate = canCreateWorkspaceTable(authz, {
        workspaceId,
        accountId,
        userInfo,
        legacyUserHasPermission: userHasPermission,
      });
      if (!canCreate) {
        const error = FORM_STUDIO_COPY.permissionDenied;
        setSubmitError(error);
        return { success: false, error };
      }
    }

    try {
      if (mode === 'create') {
        const payload = buildCreateFormPayload(form);
        const response = await formStudioService.createForm(workspaceId, payload);
        if (isFormStudioApiSuccess(response)) {
          const snapshot = normalizeFormSnapshot(form);
          setBaseline(snapshot);
          const createdFormId = extractCreatedFormId(response);
          let statusSyncWarning = '';
          if (createdFormId) {
            try {
              await syncFieldStatuses({
                formId: createdFormId,
                workspaceId,
                fields: form.leads_form_items,
              });
            } catch (syncError) {
              statusSyncWarning =
                syncError?.message || 'Form saved, but status options could not be synced.';
            }
          }
          return {
            success: true,
            response,
            createdFormId,
            statusSyncWarning,
          };
        }
        const error = extractFormStudioApiError(response?.data);
        setSubmitError(error);
        return { success: false, response, error };
      }

      const payload = buildUpdateFormPayload(form);
      const response = await formStudioService.updateForm(formId, payload);
      if (isFormStudioApiSuccess(response)) {
        const snapshot = normalizeFormSnapshot(form);
        setBaseline(snapshot);
        let statusSyncWarning = '';
        try {
          await syncFieldStatuses({
            formId,
            workspaceId,
            fields: form.leads_form_items,
          });
        } catch (syncError) {
          statusSyncWarning =
            syncError?.message || 'Form saved, but status options could not be synced.';
        }
        return { success: true, response, statusSyncWarning };
      }
      const error = extractFormStudioApiError(response?.data);
      setSubmitError(error);
      return { success: false, response, error };
    } catch (error) {
      const message = extractFormStudioApiError(error?.response?.data)
        || error?.message
        || FORM_STUDIO_COPY.saveError;
      setSubmitError(message);
      return { success: false, error: message };
    } finally {
      if (isMountedRef.current) {
        setIsSubmitting(false);
      }
    }
  }, [
    accountId,
    authz,
    form,
    formId,
    mode,
    userHasPermission,
    userInfo,
    validate,
    workspaceId,
    syncFieldStatuses,
  ]);

  const deleteForm = useCallback(async () => {
    if (!formId) {
      const error = 'Form ID is required.';
      setSubmitError(error);
      return { success: false, error };
    }

    setIsDeleting(true);
    setSubmitError('');

    try {
      const response = await formStudioService.deleteForm(formId);
      if (isFormStudioApiSuccess(response)) {
        return { success: true, response };
      }
      const error = extractFormStudioApiError(response?.data);
      setSubmitError(error);
      return { success: false, response, error };
    } catch (error) {
      const message = extractFormStudioApiError(error?.response?.data)
        || FORM_STUDIO_COPY.deleteError;
      if (isMountedRef.current) {
        setSubmitError(message);
      }
      return { success: false, error: message };
    } finally {
      if (isMountedRef.current) {
        setIsDeleting(false);
      }
    }
  }, [formId]);

  const resetDirty = useCallback(() => {
    setBaseline(normalizeFormSnapshot(form));
  }, [form]);

  const confirmDiscard = useCallback(() => {
    setForm({
      ...createEmptyFormState(),
      ...baseline,
      leads_form_items: baseline.leads_form_items ?? [],
    });
    setFieldErrors({});
    setFormErrors({});
    setValidationSummary([]);
    setSubmitError('');
  }, [baseline]);

  return {
    form,
    isLoading,
    loadError,
    isDirty,
    isSubmitting,
    isDeleting,
    submitError,
    formErrors,
    fieldErrors,
    validationSummary,
    updateFormMeta,
    setFields,
    updateField,
    addField,
    removeField,
    duplicateField,
    reorderFields,
    updateGroups,
    updateHotCriteria,
    updateTemplates,
    deletePersistedField,
    validate,
    save,
    deleteForm,
    reload: loadForm,
    resetDirty,
    confirmDiscard,
    hasFormId: Boolean(formId),
  };
}
